import React, { useState, useEffect } from 'react';
import { Shield, Monitor, History, AlertTriangle, CheckCircle, XCircle, Smartphone, MapPin, Globe, Clock, Trash2 } from 'lucide-react';
import api from '../services/apiClient';

function SecurityDashboard() {
  const [activeTab, setActiveTab] = useState('sessions');
  const [sessions, setSessions] = useState([]);
  const [loginHistory, setLoginHistory] = useState([]);
  const [securityEvents, setSecurityEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchSecurityData();
  }, []);

  const fetchSecurityData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/security/overview');
      
      // Set state with proper data access
      const data = response.data || response;
      setSessions(data.sessions || []);
      setLoginHistory(data.loginHistory || data.recentActivity?.logins || []);
      setSecurityEvents(data.securityEvents || data.recentActivity?.events || []);
    } catch (err) {
      console.error('Error fetching security data:', err.message);
      setError(err.response?.data?.error || 'Failed to load security data');
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeSession = async (sessionId) => {
    if (!confirm('Are you sure you want to revoke this session? The user will be logged out from that device.')) {
      return;
    }

    setActionLoading(true);
    try {
      await api.delete(`/security/sessions/${sessionId}`);
      await fetchSecurityData();
      alert('Session revoked successfully');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to revoke session');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevokeAllOtherSessions = async () => {
    if (!confirm('This will log you out from all other devices. Continue?')) {
      return;
    }

    setActionLoading(true);
    try {
      await api.delete('/security/sessions/other/all');
      await fetchSecurityData();
      alert('All other sessions revoked successfully');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to revoke sessions');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDeviceIcon = (deviceType) => {
    if (deviceType?.toLowerCase().includes('mobile')) {
      return <Smartphone className="w-5 h-5 text-blue-500" />;
    }
    return <Monitor className="w-5 h-5 text-blue-500" />;
  };

  const getStatusIcon = (success) => {
    return success ? 
      <CheckCircle className="w-5 h-5 text-green-500" /> : 
      <XCircle className="w-5 h-5 text-red-500" />;
  };

  const getEventIcon = (eventType) => {
    const icons = {
      'login_success': <CheckCircle className="w-5 h-5 text-green-500" />,
      'login_failed': <XCircle className="w-5 h-5 text-red-500" />,
      'account_locked': <AlertTriangle className="w-5 h-5 text-red-500" />,
      '2fa_enabled': <Shield className="w-5 h-5 text-green-500" />,
      '2fa_disabled': <Shield className="w-5 h-5 text-gray-500" />,
      'session_revoked': <Monitor className="w-5 h-5 text-orange-500" />,
      'password_changed': <Shield className="w-5 h-5 text-blue-500" />
    };
    return icons[eventType] || <Shield className="w-5 h-5 text-gray-500" />;
  };

  const renderActiveSessions = () => {
    const sessionList = Array.isArray(sessions) ? sessions : (sessions?.active || []);
    
    return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-gray-600">
          Manage your active sessions across all devices. Revoke any session you don't recognize.
        </p>
        {sessionList.filter(s => !s.isCurrent).length > 0 && (
          <button
            onClick={handleRevokeAllOtherSessions}
            disabled={actionLoading}
            className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Revoke All Other Sessions
          </button>
        )}
      </div>

      {sessionList.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Monitor className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No active sessions found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {sessionList.map((session) => (
            <div
              key={session.id}
              className={`p-4 border rounded-lg ${
                session.isCurrent ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3 flex-1">
                  {getDeviceIcon(session.deviceType)}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-gray-900">
                        {session.browser || 'Unknown Browser'} on {session.os || 'Unknown OS'}
                      </h4>
                      {session.isCurrent && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded">
                          Current Session
                        </span>
                      )}
                    </div>
                    <div className="space-y-1 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4" />
                        <span>{session.ipAddress}</span>
                        {session.location && (
                          <>
                            <MapPin className="w-4 h-4 ml-2" />
                            <span>{session.location}</span>
                          </>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span>Last active: {formatDate(session.lastActivityAt)}</span>
                      </div>
                      <div className="text-xs text-gray-500">
                        Created: {formatDate(session.createdAt)} • Expires: {formatDate(session.expiresAt)}
                      </div>
                    </div>
                  </div>
                </div>
                {!session.isCurrent && (
                  <button
                    onClick={() => handleRevokeSession(session.id)}
                    disabled={actionLoading}
                    className="ml-4 p-2 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Revoke session"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
    );
  };

  const renderLoginHistory = () => (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 mb-4">
        Review all login attempts to your account from the past 30 days.
      </p>

      {loginHistory.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <History className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No login history available</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date & Time</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Device</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP Address</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loginHistory.map((entry) => (
                <tr key={entry.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap">
                    {getStatusIcon(entry.success)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(entry.timestamp)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    <div className="flex items-center gap-2">
                      {getDeviceIcon(entry.deviceType)}
                      <div>
                        <div>{entry.browser || 'Unknown'}</div>
                        <div className="text-xs text-gray-500">{entry.os || 'Unknown OS'}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {entry.location || 'Unknown'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                    {entry.ipAddress}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderSecurityEvents = () => (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 mb-4">
        Track all security-related activities on your account.
      </p>

      {securityEvents.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <AlertTriangle className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No security events recorded</p>
        </div>
      ) : (
        <div className="space-y-3">
          {securityEvents.map((event) => (
            <div
              key={event.id}
              className="p-4 border border-gray-200 rounded-lg bg-white hover:bg-gray-50"
            >
              <div className="flex items-start gap-3">
                {getEventIcon(event.eventType)}
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="font-medium text-gray-900">
                      {event.eventType.split('_').map(word => 
                        word.charAt(0).toUpperCase() + word.slice(1)
                      ).join(' ')}
                    </h4>
                    <span className="text-sm text-gray-500">
                      {formatDate(event.createdAt)}
                    </span>
                  </div>
                  {event.description && (
                    <p className="text-sm text-gray-600 mb-2">{event.description}</p>
                  )}
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    {event.ipAddress && (
                      <span className="flex items-center gap-1">
                        <Globe className="w-3 h-3" />
                        {event.ipAddress}
                      </span>
                    )}
                    {event.location && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {event.location}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Shield className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-900">Security Dashboard</h1>
        </div>
        <p className="text-gray-600">
          Monitor and manage your account security settings and activity.
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('sessions')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'sessions'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <Monitor className="w-5 h-5" />
              <span>Active Sessions</span>
              {sessions.length > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                  {sessions.length}
                </span>
              )}
            </div>
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'history'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <History className="w-5 h-5" />
              <span>Login History</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('events')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'events'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              <span>Security Events</span>
            </div>
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        {activeTab === 'sessions' && renderActiveSessions()}
        {activeTab === 'history' && renderLoginHistory()}
        {activeTab === 'events' && renderSecurityEvents()}
      </div>
    </div>
  );
}

export default SecurityDashboard;
